import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from "@angular/core";
import { ColDef } from "ag-grid-community";

// Template UI
import { UiLibraryModule } from "@template-ui";
import { DocMeta } from "@template-ui/decorators";
import { createTrigger } from "@template-ui/helpers";
import {
  CrudApiProtocol,
  CrudFilter,
  ButtonConfig,
  EzGridDsConfig,
  InputConfig,
  MaybeSignal,
  MaybeSignalRequired,
  PanelHeaderConfig,
  ResponsiveButtonGroupConfig,
  NamedEvent
} from "@template-ui/types";

import { AppBaseComponent } from "../app-base.component";
import { ActivityLogGridDialogService } from "./activity-log-grid-dialog.service";
import { ActivityLogGridConfigService } from "./activity-log-grid-config.service";
import {
  ActivityLogAddActivityEndpointParamConfig,
  ActivityLogContext,
  ActivityLogType,
  DEFAULT_ACTIVITY_LOG_COL_DEFS
} from "./activity-log-grid.types";

/**
 * Shared activity-log grid with standardized Add Activity behavior.
 *
 * ## Step 1: Basic grid setup (read-only)
 * Start with preset configuration to resolve read endpoint, columns, filters, and settings key.
 *
 * ```ts
 * protected readonly activityLogType = ActivityLogType.LegalDecisionReview;
 * protected readonly activityLogContext = computed(() => ({
 *   cdcNum: this.appState.cdcNumSignal(),
 *   planId: this.appState.planIdSignal(),
 * }));
 * ```
 *
 * ```html
 * <app-activity-log-grid
 *   [activityLogType]="activityLogType"
 *   [activityLogContext]="activityLogContext()"
 * />
 * ```
 *
 * ## Step 2: Enable Add Activity
 * Add Activity is shown only when:
 * 1. An add endpoint is resolved (preset or explicit input), and
 * 2. `hideAddActivity` is false.
 *
 * If the button is missing, check those two conditions first.
 *
 * For explicit override usage, pass `addActivityEndpoint` and optional context-to-payload
 * mappings via `addActivityEndpointParams`.
 *
 * ## Override usage (explicit endpoint path)
 * Use this when a feature needs custom endpoints or payload keys that differ from presets.
 *
 * ```ts
 * protected readonly customContext = computed(() => ({
 *   recordId: this.appState.idSignal(),
 *   offenderNumber: this.appState.cdcNumSignal(),
 * }));
 *
 * protected readonly addActivityEndpointParams = [
 *   { contextKey: "recordId", formDataKey: "CaseId", required: true },
 *   { contextKey: "offenderNumber", formDataKey: "CdcNum", required: true },
 * ];
 * ```
 *
 * ```html
 * <app-activity-log-grid
 *   [readEndpoint]="readEndpoint"
 *   [addActivityEndpoint]="addEndpoint"
 *   [activityLogContext]="customContext()"
 *   [addActivityEndpointParams]="addActivityEndpointParams"
 *   [documentTypesModule]="'LEGAL'"
 * />
 * ```
 */
@DocMeta({
  category: "Components",
  title: "Activity Log Grid",
  showcaseComponentNames: ["ActivityLogGridSampleComponent"],
})
@Component({
  selector: "app-activity-log-grid",
  imports: [UiLibraryModule],
  templateUrl: "./activity-log-grid.component.html",
  styleUrl: "./activity-log-grid.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityLogGridComponent extends AppBaseComponent {
  // ----------------------------------------------------------------
  // Inputs, Outputs, Models, and Public Signals
  // ----------------------------------------------------------------

  /** Endpoint used by ez-grid to fetch activity rows. */
  readonly readEndpoint = input<MaybeSignal<string>>(undefined);

  /** Optional enum-based activity-log variant. Used to resolve endpoints via service. */
  readonly activityLogType = input<ActivityLogType | null>(null);

  /** Optional endpoint used to submit Add Activity form data; when omitted, Add Activity is hidden. */
  readonly addActivityEndpoint = input<MaybeSignal<string>>(undefined);
  /** Optional Add Activity endpoint context mappings (context key -> payload key). */
  readonly addActivityEndpointParams = input<ActivityLogAddActivityEndpointParamConfig[] | null>(null);

  /** Module code used to fetch doc types when endpoint mode is used. */
  readonly documentTypesModule = input<string | null>(null);

  /** Optional direct endpoint for doc types. Overrides `documentTypesModule` when provided. */
  readonly documentTypesEndpoint = input<string | null>(null);

  /** Optional static doc types payload (array or wrapped). Used instead of endpoint when set. */
  readonly staticDocTypes = input<unknown | null>(null);

  /** Optional context used to resolve type-based configuration (for example `{ caseId }`, `{ planId }`). */
  readonly activityLogContext = input<MaybeSignal<ActivityLogContext>>(undefined);

  /** Optional panel header title override. */
  readonly panelHeaderTitle = input<MaybeSignal<string>>("Activity Log");
  /** Optional settings key override used by panel-header filter persistence. */
  readonly settingsKey = input<MaybeSignal<string>>(undefined);

  /** Optional filter input configs shown in the panel header filter area. */
  readonly filterInputConfigs = input<InputConfig[] | null>(null, { alias: "filterInputsConfigs" });

  /** Optional column definitions override for feature-specific activity-log layouts. */
  readonly columnDefs = input<ColDef[] | null>(null);

  /** Optional empty-state message override. */
  readonly noDataFoundMessage = input<MaybeSignalRequired<string>>("No activity found.");

  /** Sort field for initial grid load. */
  readonly sortBy = input<string>("addDate");

  /** Sort direction for initial grid load. */
  readonly sortDir = input<"asc" | "desc">("desc");

  /** Protocol used by the READ binding for endpoint calls. */
  readonly readEndpointProtocol = input<CrudApiProtocol | undefined>(undefined);

  /** Optional static filters applied to every load request. */
  readonly staticFilters = input<{ [key: string]: CrudFilter } | undefined>(undefined);

  /**
   * Hides the Add Activity action when true.
   *
   * The Add Activity button is shown only when both conditions are met:
   * 1) An `addActivityEndpoint` is resolved, and
   * 2) `hideAddActivity` resolves to false.
   *
   * If the button is missing, check those two conditions first.
   */
  readonly hideAddActivity = input<MaybeSignal<boolean>>(false);

  /** Code that triggers display/validation of Other Description. */
  readonly otherDocumentTypeCode = input<string>("OTHER");

  // ----------------------------------------------------------------
  // Public Template-Bound Properties
  // ----------------------------------------------------------------

  readonly activityLogConfig = computed<EzGridDsConfig>(() => ({
    datasourceId: "ActivityLog",
    settingsKey: this.resolvedSettingsKey(),
    bindings: [{ bindingType: "READ", protocol: this.resolvedReadEndpointProtocol(), endpoint: this.resolvedReadEndpoint() }],
    colDefs: this.resolvedColumnDefs(),
    defaultSortBy: this.sortBy(),
    defaultSortOrder: this.sortDir(),
    staticFilters: this.resolvedStaticFilters(),
  }));

  readonly panelHeaderConfig = computed<PanelHeaderConfig>(() => ({
    panelHeaderTitle: this.read(this.panelHeaderTitle()),
    settingsKey: this.resolvedSettingsKey(),
    showStandardFilterControls: !!this.resolvedFilterInputConfigs()?.length,
    filterInputConfigs: this.resolvedFilterInputConfigs() ?? undefined,
    additionalActions: this.showAddActivityButton()
      ? ({
        buttonsConfig: [
          {
            id: "addActivity",
            buttonText: "Add Activity",
          } as ButtonConfig
        ]
      } as ResponsiveButtonGroupConfig)
      : undefined
  }));
  readonly resolvedNoDataFoundMessage = computed<string>(() => this.read(this.noDataFoundMessage()));

  protected readonly reloadGridTrig = createTrigger();

  // ----------------------------------------------------------------
  // Private/Internal State
  // ----------------------------------------------------------------

  private readonly activityLogGridDialogService = inject(ActivityLogGridDialogService);
  private readonly activityLogGridConfigService = inject(ActivityLogGridConfigService);

  private docTypesFromEndpoint: unknown = [];
  private readonly resolvedDocTypes = computed(() => this.staticDocTypes() ?? this.docTypesFromEndpoint);
  private readonly resolvedActivityLogContext = computed<ActivityLogContext>(() => this.read(this.activityLogContext()) ?? {});
  private readonly resolvedEndpointConfig = computed(() => {
    const type = this.activityLogType();
    if (!type) {
      return null;
    }

    return this.activityLogGridConfigService.getConfig(type, this.resolvedActivityLogContext());
  });
  private readonly resolvedReadEndpoint = computed(() => this.read(this.readEndpoint()) ?? this.resolvedEndpointConfig()?.readEndpoint ?? "");
  private readonly resolvedReadEndpointProtocol = computed<CrudApiProtocol>(() =>
    this.readEndpointProtocol() ?? this.resolvedEndpointConfig()?.readEndpointProtocol ?? "GET"
  );
  private readonly resolvedSettingsKey = computed(() => this.read(this.settingsKey()) ?? this.resolvedEndpointConfig()?.settingsKey);
  private readonly resolvedAddActivityEndpoint = computed(() =>
    this.read(this.addActivityEndpoint()) ?? this.resolvedEndpointConfig()?.addActivityEndpoint ?? undefined
  );
  private readonly resolvedAddActivityEndpointParams = computed<ActivityLogAddActivityEndpointParamConfig[] | undefined>(() =>
    this.addActivityEndpointParams() ?? this.resolvedEndpointConfig()?.addActivityEndpointParams
  );
  private readonly resolvedDocumentTypesModule = computed(() =>
    this.documentTypesModule() ?? this.resolvedEndpointConfig()?.documentTypesModule ?? null
  );
  private readonly resolvedDocumentTypesEndpoint = computed(() =>
    this.documentTypesEndpoint() ?? this.resolvedEndpointConfig()?.documentTypesEndpoint ?? null
  );
  private readonly resolvedColumnDefs = computed<ColDef[]>(() =>
    this.columnDefs() ?? this.resolvedEndpointConfig()?.colDefs ?? DEFAULT_ACTIVITY_LOG_COL_DEFS
  );
  private readonly resolvedStaticFilters = computed<{ [key: string]: CrudFilter } | undefined>(
    () => this.staticFilters() ?? this.resolvedEndpointConfig()?.staticFilters
  );
  private readonly resolvedFilterInputConfigs = computed<InputConfig[] | null>(
    () => this.filterInputConfigs() ?? this.resolvedEndpointConfig()?.filterInputConfigs ?? null
  );
  private readonly hasAddActivityEndpoint = computed(() => !!this.resolvedAddActivityEndpoint());
  private readonly showAddActivityButton = computed(() => this.hasAddActivityEndpoint() && !this.read(this.hideAddActivity()));

  // ----------------------------------------------------------------
  // Lifecycle Hooks
  // ----------------------------------------------------------------

  constructor() {
    super();

    effect(() => {
      this.loadDocTypes();
    });
  }

  // ----------------------------------------------------------------
  // Public Methods
  // ----------------------------------------------------------------

  onPanelHeaderButtonClick(e: NamedEvent | ButtonConfig): void {
    if (!this.showAddActivityButton()) {
      return;
    }

    if ("name" in e && e.name === "addActivity") {
      this.openAddActivityDialog();
      return;
    }

    if ("id" in e && e.id === "addActivity") {
      this.openAddActivityDialog();
    }
  }

  // ----------------------------------------------------------------
  // Private Helper Methods
  // ----------------------------------------------------------------

  private loadDocTypes(): void {
    if (this.staticDocTypes()) {
      return;
    }

    const explicitEndpoint = this.resolvedDocumentTypesEndpoint();
    const moduleCode = this.resolvedDocumentTypesModule();
    const endpoint =
      explicitEndpoint ||
      (moduleCode ? this.apis.core.getDocumentGetDocumentTypesForUploadByModule(moduleCode) : null);

    if (!endpoint) {
      this.docTypesFromEndpoint = [];
      return;
    }

    this.crudService.getbyUrl(endpoint).pipe(this.takeUntilDestroyed()).subscribe({
      next: (resp: unknown) => {
        this.docTypesFromEndpoint = resp;
      },
      error: () => {
        this.docTypesFromEndpoint = [];
      }
    });
  }

  private openAddActivityDialog(): void {
    const submitEndpoint = this.resolvedAddActivityEndpoint();
    if (!submitEndpoint) {
      return;
    }

    void this.activityLogGridDialogService.openActivityLogDialog({
      activityLogContext: this.resolvedActivityLogContext(),
      addActivityEndpointParams: this.resolvedAddActivityEndpointParams(),
      docTypes: this.resolvedDocTypes(),
      submitEndpoint,
      postByUrl: (endpoint: string, formData: FormData) => this.crudService.postByUrl(endpoint, formData),
      otherDocumentTypeCode: this.otherDocumentTypeCode(),
      header: "Add Activity",
    }).then((completion) => {
      if (completion?.status === "success") {
        this.reloadGridTrig.pulse();
      }
    });
  }
}
